/**
 * Records short audio clips from the browser mic using MediaRecorder,
 * returns a Blob when stopped. Designed to be used from a React hook.
 */
export class MicRecorder {
  constructor() {
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
    this.mimeType = "";
  }

  static isSupported() {
    return typeof window !== "undefined"
      && !!navigator.mediaDevices
      && typeof window.MediaRecorder !== "undefined";
  }

  async start() {
    this.chunks = [];
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // Pick the first supported mimeType — Whisper accepts webm/mp4/mpeg.
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/mpeg",
    ];
    this.mimeType =
      candidates.find((t) => window.MediaRecorder.isTypeSupported?.(t)) || "";

    this.recorder = this.mimeType
      ? new MediaRecorder(this.stream, { mimeType: this.mimeType })
      : new MediaRecorder(this.stream);

    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.recorder.start();
  }

  /**
   * Stops recording and resolves with a Blob (the recorded audio).
   */
  stop() {
    return new Promise((resolve, reject) => {
      if (!this.recorder) {
        reject(new Error("Recorder not started"));
        return;
      }
      this.recorder.onstop = () => {
        const type = this.mimeType || "audio/webm";
        const blob = new Blob(this.chunks, { type });
        this._cleanup();
        resolve(blob);
      };
      this.recorder.onerror = (e) => {
        this._cleanup();
        reject(e.error || new Error("MediaRecorder error"));
      };
      try {
        this.recorder.stop();
      } catch (e) {
        this._cleanup();
        reject(e);
      }
    });
  }

  _cleanup() {
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    this.recorder = null;
  }
}

export function extensionFor(mimeType) {
  if (!mimeType) return "webm";
  if (mimeType.includes("webm")) return "webm";
  if (mimeType.includes("mp4")) return "m4a";
  if (mimeType.includes("mpeg")) return "mp3";
  if (mimeType.includes("wav")) return "wav";
  return "webm";
}
